/**
 * Live or still (pure): the live captain is a second, small WebGL canvas, so he is drawn live only when motion is
 * welcome, the quality tier can afford it and WebGL works; otherwise the static renders stand in. Also the canvas's
 * pixel ratio and which static file to ask for.
 */
import { BRAM_ART_1X_PX, bramArtPath, legacyArtPath, type BramArtPose } from "./bramContract";

export type TierBandName = "ultra" | "high" | "medium" | "low" | "lowest";

export interface BramEnv {
  /** The OS asks for reduced motion. */
  readonly reducedMotion: boolean;
  /** Settings → Reduce effects. */
  readonly reducedVfx: boolean;
  readonly band: TierBandName;
  /** WebGL could not start, or the model failed to load, earlier this session. */
  readonly failed: boolean;
}

export type BramMode = "live" | "static";

export function bramMode(env: BramEnv): BramMode {
  if (env.reducedMotion || env.reducedVfx || env.failed || env.band === "lowest") return "static";
  return "live";
}

/**
 * The canvas's pixel ratio: the device's, capped by the tier, never above 2. The welcome figure is ~300 px square,
 * and at 3× (867 px square) it was as many pixels as the whole 1.5× game view behind it: +31 % on the frame while he
 * stood there (RC1 perf audit). At 2× he is still sharper than the scene (1.5× on `high`) for 44 % of those pixels.
 */
export const BRAM_DPR_CAP: Readonly<Record<TierBandName, number>> = { ultra: 2, high: 2, medium: 1.5, low: 1.25, lowest: 1 };
export function bramPixelRatio(deviceDpr: number, band: TierBandName): number {
  return Math.max(1, Math.min(deviceDpr || 1, BRAM_DPR_CAP[band]));
}

/** Frame rates: idle breathing does not need 60 (a low tier stays at the idle rate throughout). */
export const BRAM_FPS = { Idle: 30, Active: 60 } as const;
export function bramFps(active: boolean, band: TierBandName): number {
  return active && band !== "low" ? BRAM_FPS.Active : BRAM_FPS.Idle;
}

/**
 * Static files to try in order for a pose drawn `cssPx` tall: the new render at the size it needs (`@1x` while that is
 * sharp, `@3x` beyond), then the old bust (until the new renders ship, and if one is ever missing).
 */
export function staticArt(pose: BramArtPose, cssPx: number, deviceDpr: number): string[] {
  const need = cssPx * Math.max(1, deviceDpr || 1);
  return [bramArtPath(pose, need <= BRAM_ART_1X_PX ? 1 : 3), legacyArtPath(pose)];
}
